import { Doughnut } from "react-chartjs-2";
import React, { useCallback, useRef, useEffect } from "react";
import { DoughnutSizes, PercentageDoughnutProps } from "./types";
import { Chart, ArcElement } from "chart.js";
Chart.register(ArcElement);

const sizes: {
  [key in DoughnutSizes]: {
    canvas: number;
    fontSize: number;
    lineHeight: number;
    labelWidth: number;
    extraPosPixel: number;
  };
} = {
  small: {
    canvas: 56,
    fontSize: 14,
    lineHeight: 20,
    labelWidth: 35,
    extraPosPixel: 1,
  },
  medium: {
    canvas: 112,
    fontSize: 32,
    lineHeight: 40,
    labelWidth: 70,
    extraPosPixel: 2,
  },
  large: {
    canvas: 224,
    fontSize: 64,
    lineHeight: 85,
    labelWidth: 140,
    extraPosPixel: 3,
  },
};

const PercentageDoughnut = ({
  color,
  completed,
  size = "small",
  testId,
}: PercentageDoughnutProps) => {
  const percentageLabelRef = useRef<HTMLParagraphElement>(null);

  const width = sizes[size].labelWidth;
  const height = sizes[size].lineHeight;
  const addLabel = useCallback(
    (context) => {
      if (percentageLabelRef.current) {
        percentageLabelRef.current.textContent = `${context.data.datasets[0].data[0]}%`;
        const xPos = context.width / 2 - width / 2 + sizes[size].extraPosPixel;
        const yPos =
          context.height / 2 + height / 2 + sizes[size].extraPosPixel;
        percentageLabelRef.current.style.left = `${xPos}px`;
        percentageLabelRef.current.style.top = `-${yPos}px`;
      }
    },
    [height, size, width]
  );

  useEffect(() => {
    if (percentageLabelRef.current) {
      percentageLabelRef.current.textContent = `${completed}%`;
    }
  }, [completed]);

  return (
    <div
      style={{
        width: sizes[size].canvas,
        height: sizes[size].canvas,
        position: "relative",
      }}
      data-testId={testId}
    >
      <Doughnut
        style={{ position: "relative", zIndex: "1" }}
        data={{
          datasets: [
            {
              data: [completed, 100 - completed],
              borderColor: "transparent",
              backgroundColor: [color, "transparent"],
              borderRadius: [completed === 100 ? 0 : 1000, 0],
            },
          ],
        }}
        options={{
          animation: { delay: 100, duration: 2000 },
          aspectRatio: 1,
          cutout: "75%",
          onResize: addLabel,
          plugins: {
            tooltip: { enabled: false },
            legend: { display: false },
            datalabels: {
              display: false,
            },
          },
        }}
      />
      <Doughnut
        style={{ position: "absolute", zIndex: "0", top: "0" }}
        data={{
          datasets: [
            {
              data: [100],
              borderColor: "transparent",
              backgroundColor: ["#DADAE4", "transparent"],
              borderRadius: 0,
            },
          ],
        }}
        options={{
          animation: { delay: 0, duration: 0 },
          aspectRatio: 1,
          cutout: "75%",
          plugins: {
            tooltip: { enabled: false },
            legend: { display: false },
            datalabels: {
              display: false,
            },
          },
        }}
      />

      <p
        style={{
          width: `${width}px`,
          height: `${height}px`,
          position: "relative",
          fontSize: `${sizes[size].fontSize}px`,
          fontWeight: 800,
          lineHeight: `${sizes[size].lineHeight}px`,
          margin: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#4d4d4d",
        }}
        data-testId={`${testId}-value`}
        ref={percentageLabelRef}
      />
    </div>
  );
};

export default PercentageDoughnut;
